import { useState, useRef, useEffect } from 'react';
import { Form, ListGroup, Badge } from 'react-bootstrap';
import { X } from 'lucide-react';
import type { Brand } from '../../../../apps/hooks/oms/useOrderSegment';

interface BrandComboboxProps {
  brands: Brand[];
  selectedBrands: Brand[];
  onChange: (brands: Brand[]) => void;
  disabled?: boolean;
  required?: boolean;
}

const BrandCombobox = ({ brands, selectedBrands, onChange, disabled, required }: BrandComboboxProps) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = query.length > 0
    ? brands
        .filter(b => b.name.toLowerCase().includes(query.toLowerCase()))
        .filter(b => !selectedBrands.some(s => s.termId === b.termId))
        .slice(0, 20)
    : [];

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (brand: Brand) => {
    onChange([...selectedBrands, brand]);
    setQuery('');
    setIsOpen(false);
  };

  const handleRemove = (brand: Brand) => {
    onChange(selectedBrands.filter(b => b.termId !== brand.termId));
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <Form.Control
        type="text"
        placeholder="Search brands..."
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => { if (query.length > 0) setIsOpen(true); }}
        disabled={disabled ?? false}
        required={(required ?? false) && selectedBrands.length === 0}
      />
      {isOpen && filtered.length > 0 && (
        <ListGroup
          style={{
            position: 'absolute',
            zIndex: 1000,
            width: '100%',
            maxHeight: '240px',
            overflowY: 'auto',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          {filtered.map(brand => (
            <ListGroup.Item
              key={brand.termId}
              action
              onClick={() => handleSelect(brand)}
              style={{ cursor: 'pointer' }}
            >
              {brand.name}
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
      {selectedBrands.length > 0 && (
        <div className="mt-2 d-flex flex-wrap gap-1">
          {selectedBrands.map(brand => (
            <Badge
              key={brand.termId}
              bg="primary"
              className="d-inline-flex align-items-center gap-1 px-2 py-1"
              style={{ fontSize: '0.85rem' }}
            >
              {brand.name}
              <X
                size={14}
                style={{ cursor: 'pointer' }}
                onClick={() => handleRemove(brand)}
              />
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

export default BrandCombobox;
